<template>
  <div style="max-width: 600px;">
    <b>{{ $t('EXPORT.EXPORT_CSV') }}</b>
    <v-card class="mt-2 pa-6">
      <btn-toggle-field v-model="type" :items="[{ value: 'single', title: $t('EXPORT.SIMPLE_EXPORT') }, { value: 'multiple', title: $t('EXPORT.MULTIPLE_EXPORT') }]"></btn-toggle-field>
      <div v-if="type == 'multiple'" class="mt-2">
        <div class="d-flex flex-wrap column-gap-4">
          <v-checkbox v-for="file in availableFiles" :key="file.id" v-model="selectedFiles" :label="$t(file.title)" :value="file.id" density="compact"></v-checkbox>
        </div>
      </div>
      <div class="mt-4 d-flex align-center">
        <label for="separator">{{ $t('EXPORT.CSV_SEPARATOR') }}</label>
        <div class="ml-4" style="max-width: 50px">
          <v-text-field id="separator" v-model="csvSeparator" density="compact" class="text-center"></v-text-field>
        </div>
      </div>
      <div class="mt-6">
        <v-btn color="primary" rounded size="large" style="min-width: 180px" @click="exportCsv()">{{ $t('EXPORT.EXPORT') }}</v-btn>
      </div>
      <ol v-if="type == 'multiple' && downloadLinks.length" class="mt-6">
        <li v-for="link in downloadLinks" :key="link.filename" class="ml-4 mb-1">
          <a :href="link.encodedUri" :download="link.filename">{{ link.filename }}</a>
        </li>
      </ol>
    </v-card>
  </div>
</template>

<script>
  import { mapState } from 'pinia';
  import Planning from '@/models/Planning';
  import Action from '@/models/Action';
  import { workloadToDisplay } from '@/components/Workload/workloadHelpers';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import useUsersStore from '@/stores/users';
  import useSubprojectsStore from '@/stores/subprojects';

  export default {
    props: {
      planning: { type: Object, required: true },
    },
    data() {
      const ED = 'ELEMENT-DETAIL.';
      const MACSV = 'MONITORING_ACTIONS.CSV.';
      const EXPCSV = 'EXPORT.CSV.';

      return {
        type: 'single',
        csvSeparator: ';',
        availableFiles: [
          {
            id: 'elements',
            title: 'MULTIPROJECTS.BOTH',
            fields: ['id', 'lane', 'title', 'type', 'starttime', 'endtime', 'schedule.start', 'schedule.end', 'html', 'progress'],
            texts: [`${EXPCSV}ELEMENT_ID`, 'PLANNING.LANE', `${ED}TITLE`, `${EXPCSV}TYPE`, `${ED}FROM`, `${ED}TO`, `${ED}SCHEDULE_START`, `${ED}SCHEDULE_END`, `${ED}DESCRIPTION`, `${ED}PROGRESS`],
          },
          {
            id: 'actions',
            title: `${ED}CHECKLIST`,
            fields: ['title', 'lane', 'element', 'element_id', 'username', 'duedate', 'checked', 'workload', 'workload_used'],
            texts: [`${MACSV}GETTITLE`, `${MACSV}GETLANEID`, `${EXPCSV}ELEMENT`, `${EXPCSV}ELEMENT_ID`, `${MACSV}GETUSERID`, `${MACSV}GETDUEDATE`, `${MACSV}GETCHECKED`, `${MACSV}GETWORKLOAD`, `${MACSV}GETWORKLOADUSED`],
          },
          {
            id: 'users',
            title: `${ED}USERS`,
            fields: ['username', 'email', 'type', 'element', 'element_id'],
            texts: [`${EXPCSV}USERNAME`, `GLOBAL.EMAIL`, `${EXPCSV}TYPE`, `${EXPCSV}ELEMENT`, `${EXPCSV}ELEMENT_ID`],
          },
          {
            id: 'links',
            title: `${ED}ATTACHMENTS`,
            fields: ['title', 'url', 'element', 'element_id'],
            texts: [`${ED}LINK_NAME`, `${EXPCSV}URL`, `${EXPCSV}ELEMENT`, `${EXPCSV}ELEMENT_ID`],
          },
          {
            id: 'budgets',
            title: `${ED}BUDGET`,
            fields: ['amount_inprogress', 'amount', 'icon', 'state', 'lane', 'element', 'element_id'],
            texts: [`${ED}AMOUNT_INPROGRESS`, `${ED}AMOUNT`, `COLORPICKER.ICON`, `MANAGE.CONFIG.BUDGET_STATE`, `${MACSV}GETLANEID`, `${EXPCSV}ELEMENT`, `${EXPCSV}ELEMENT_ID`],
          },
        ],
        selectedFiles: ['elements', 'actions', 'users', 'links', 'budgets'],
        singleFile: {
          id: 'elements',
          title: '',
          extractFn: this.elementsToCsv,
          fields: ['lane', 'title', 'type', 'starttime', 'endtime', 'schedule.start', 'schedule.end', 'html', 'progress', 'checklist', 'users', 'links', 'budgets'],
          texts: ['PLANNING.LANE', `${ED}TITLE`, `${EXPCSV}TYPE`, `${ED}FROM`, `${ED}TO`, `${ED}SCHEDULE_START`, `${ED}SCHEDULE_END`, `${ED}DESCRIPTION`, `${ED}PROGRESS`, `${ED}CHECKLIST`, `${ED}USERS`, `${ED}ATTACHMENTS`, `${ED}BUDGET`],
        },
        downloadLinks: [], // for multiple files
      };
    },
    computed: {
      safeProjectName() {
        return this.planning.meta.title.replace(/<(?:.|\n)*?>/gm, ' ').replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/\//g, ' ');
      },
      subPlannings() {
        return this.planning.lanes.filter(lane => lane.project_id).map((lane) => {
          const subPlanning = new Planning(this.getSubprojectByLaneId(lane.id));
          subPlanning.lanes = subPlanning.lanes.filter((subLane) => {
            const subprojectLaneOriginalId = parseInt(subLane.id, 10);
            return ! (lane.project_hidden_sublanes && lane.project_hidden_sublanes.includes(subprojectLaneOriginalId));
          }).map((subLane) => {
            subLane.label = `${lane.label} ↪ ${subLane.label}`;
            return subLane;
          });
          return subPlanning;
        });
      },
      budgetStates() {
        return useUsersAccessRightStore().config.budgetStates;
      },
      ...mapState(useSubprojectsStore, ['getSubprojectByLaneId']),
    },
    methods: {
      getElPublicId(planning, el) {
        return `el_${planning.id.toString(36)}_${(el.o_id || el.id).toString(36)}`;
      },
      getPlanningData(planning) {
        const usersStore = useUsersStore();
        const lanes = {};
        const lanesIndex = {};
        planning.lanes.forEach((lane, index) => {
          lanes[lane.id] = lane;
          lanesIndex[lane.id] = index;
        });
        const elements = [];
        const actions = [];
        const users = [];
        const links = [];
        const budgets = [];

        planning.elements.filter(el => lanes[el.getLaneId()]).sort(
          (a, b) => (lanesIndex[a.getLaneId()] - lanesIndex[b.getLaneId()]) || (a.getStartTime() < b.getStartTime() ? -1 : 1),
        ).forEach((el) => {
          const elementId = this.getElPublicId(planning, el);

          elements.push({
            id: elementId,
            lane: (lanes[el.getLaneId()] || {}).label || "",
            title: el.getTitle(),
            type: el.isType('macro') ? this.$t('GLOBAL.MACRO_BUBBLE') : el.isType('milestone') ? this.$t('GLOBAL.MILESTONE') : this.$t('GLOBAL.BUBBLE'),
            starttime: el.getStartTime()?.format('L'),
            endtime: el.getEndTime()?.format('L'),
            'schedule.start': el.getSchedule().start?.format('HH:mm'),
            'schedule.end': el.getSchedule().end?.format('HH:mm'),
            html: el.getDescription(),
            progress: el.getProgress(),
            checklist: el.getChecklist()?.map(item => (item?.title ? `- ${item.title}` : '')).join('\n'),
            users: el.getUsers()?.map(user => usersStore.getUsername(user) || '').join('\n'),
            links: el.getLinks()?.map(link => link?.url || '').join('\n'),
            budgets: el.getBudgets()?.map((a) => {
              if (! a) return '';
              return (a.amount_inprogress ? `${a.amount_inprogress}/` : '') + (a.amount || '') + (a.icon ? ` ${a.icon}` : '') + (a.state ? ` (${a.state})` : '');
            }).join('\n'),
          });

          el.getChecklist()?.forEach((item) => {
            const action = new Action({ type: 'planning', element: el, item });
            let username = usersStore.getUsername({ id: item.user_id });
            if (! username && item.user_id && item.username) username = `${this.$t('MULTIPROJECTS.DELETED_USER')} (${item.username})`;
            if (! username && item.username) username = item.username;

            let duedate = moment(action.getDuedate());
            duedate = duedate.isValid() ? duedate.format('L') : action.getDuedate();

            actions.push({
              title: action.getTitle(),
              lane: (lanes[el.getLaneId()] || {}).label,
              element: el.getTitle(),
              element_id: elementId,
              username,
              duedate,
              checked: action.getChecked(),
              workload: workloadToDisplay(action.getWorkload()),
              workload_used: workloadToDisplay(action.getWorkloadUsed()),
            });
          });

          el.getUsers()?.forEach((user) => {
            let username = usersStore.getUsername(user);
            if (user.id && ! usersStore.getUserById(user.id)) username = `${this.$t('MULTIPROJECTS.DELETED_USER')} (${username})`;

            users.push({
              username,
              email: user.email,
              type: user.id ? this.$t('MANAGE.CONFIG.REPLACE_USER.REGISTERED_USER') : user.group_id ? this.$t('GLOBAL.GROUP') : this.$t('MANAGE.CONFIG.REPLACE_USER.VIRTUAL_PARTICIPANT'),
              element: el.getTitle(),
              element_id: elementId,
            });
          });

          el.getLinks()?.forEach((link) => {
            links.push({
              title: link.name,
              url: link.url,
              element: el.getTitle(),
              element_id: elementId,
            });
          });

          el.getBudgets()?.forEach((budget) => {
            budgets.push({
              amount_inprogress: budget.amount_inprogress,
              amount: budget.amount,
              icon: budget.icon,
              state: this.budgetStates[budget.state] || budget.state,
              lane: (lanes[el.getLaneId()] || {}).label,
              element: el.getTitle(),
              element_id: elementId,
            });
          });
        });
        return {
          elements,
          actions,
          users,
          links,
          budgets,
        };
      },
      exportCsv() {
        const planningData = this.getPlanningData(this.planning);
        const subPlanningsData = this.subPlannings.map(subPlanning => this.getPlanningData(subPlanning));

        const exportFiles = this.type == 'multiple' ? this.availableFiles.filter(file => this.selectedFiles.includes(file.id)) : [this.singleFile];
        this.downloadLinks = exportFiles.map((file) => {
          if (file.texts.length != file.fields.length) console.error(`file ${file.id} has different number of texts and fields`);
          let csvContent = `${file.texts.reduce((a, b) => (a ? a + this.csvSeparator : '') + this.$t(b), '')}\n`;
          planningData[file.id].forEach((rowData) => {
            csvContent += file.fields.map((field) => {
              let val = rowData[field];
              if (this.csvSeparator == ';' && typeof val == 'number') val = val.toString().replace('.', ',');
              if (val === true) val = 'X';
              val = (val || '').toString();
              return `"${["-", "+"].indexOf(val[0]) > -1 ? ' ' : ''}${val.replace(/"/g, '""')}"`;
            }).join(this.csvSeparator);
            csvContent += '\n';
          });
          subPlanningsData.forEach((subPlanningData) => {
            subPlanningData[file.id].forEach((rowData) => {
              csvContent += file.fields.map((field) => {
                let val = rowData[field];
                if (this.csvSeparator == ';' && typeof val == 'number') val = val.toString().replace('.', ',');
                if (val === true) val = 'X';
                val = (val || '').toString();
                return `"${["-", "+"].indexOf(val[0]) > -1 ? ' ' : ''}${val.replace(/"/g, '""')}"`;
              }).join(this.csvSeparator);
              csvContent += '\n';
            });
          });

          return {
            encodedUri: encodeURI(`data:text/csv;charset=utf-8,\uFEFF${csvContent}`).replace(/#/g, '%23'),
            filename: file.title ? `${this.safeProjectName} - ${this.$t(file.title)}.csv` : `${this.safeProjectName}.csv`,
          };
        });

        if (this.type == 'single') {
          // autodownload
          const link = document.createElement("a");
          link.setAttribute("href", this.downloadLinks[0].encodedUri);
          link.setAttribute("download", this.downloadLinks[0].filename);
          document.body.appendChild(link); // Required for FF
          link.click();
          document.body.removeChild(link);
          this.downloadLinks = []; // do not show download links
        }
      },
    },
  };
</script>
